import { DateTime } from 'luxon'
import { Shift } from '~/services/apiClient'

export function shiftFromTimeAndShift(date: DateTime, shift: Shift): string {
  const dateStr = date.toFormat('ccc M/d')
  const shiftDesc = shiftNameFromShift(shift)
  return `${dateStr} - ${shiftDesc}`
}
export function multiShiftSlotKey(date: DateTime): string {
  return `${date.toFormat('ccc M/d')}-multi`
}
export function shiftNameFromShift(shift: Shift): string {
  let shiftDesc: string
  switch (shift) {
    case Shift.First:
      shiftDesc = '1st'
      break
    // For the main app, I had made the decision to combine 2nd and 3rd shifts
    case Shift.Second:
    case Shift.Third:
      shiftDesc = '2nd'
      break
  }
  return `${shiftDesc} shift`
}

export function shortShiftName(shift: Shift): string {
  switch (shift) {
    case Shift.First:
      return '1st'
    case Shift.Second:
      return '2nd'
    case Shift.Third:
      return '3rd'
  }
}

const FIRST_SHIFT_START_HOUR = 6
const SECOND_SHIFT_START_HOUR = 17
const THIRD_SHIFT_START_HOUR = 3

// How long the just-ended shift stays treated as "current" in the Schedule view,
// so containers worked late into a shift don't vanish the moment the next one starts.
export const PREVIOUS_SHIFT_VISIBILITY_HOURS = 3

/**
 * Whether a shift group should still appear in the Schedule view.
 *
 * This is the gate that actually controls visibility of past shifts: a shift
 * stays visible until PREVIOUS_SHIFT_VISIBILITY_HOURS after its window ends, so
 * containers worked late into a shift don't disappear the moment it's over.
 *
 * @param windowEnd - The shift group's end time.
 * @param now - The reference "current" time (injectable for tests).
 */
export function isShiftGroupVisible(
  windowEnd: DateTime,
  now: DateTime = DateTime.now()
): boolean {
  return windowEnd > now.minus({ hours: PREVIOUS_SHIFT_VISIBILITY_HOURS })
}

export function shiftFromTime(dt: DateTime): [DateTime, Shift] {
  const shiftStart = shiftStartFromTime(dt)
  const shift = shiftFromHour(shiftStart.hour)
  // Use the shift start date for grouping to ensure appointments from different shifts
  // don't get mixed together. For example:
  // - July 19 2am appointment → shift started July 18 5pm → grouped under July 18
  // - July 19 11pm appointment → shift started July 19 5pm → grouped under July 19
  // This prevents mixing appointments from different actual shifts.
  // The display date (for headers) is handled separately in ShiftGroup.
  let shiftDate = shiftStart.startOf('day')
  // Third shift (3am-6am) is the tail end of the previous day's overnight shift.
  // Group it under the previous day so it sorts after the 2nd shift and before
  // the next day's 1st shift. For example:
  // - Wed 5am → 3rd shift started Wed 3am → grouped under Tue (previous day)
  // This ensures the sort order is: Tue 1st → Tue 2nd → Tue 3rd → Wed 1st
  if (shift === Shift.Third) {
    shiftDate = shiftDate.minus({ days: 1 })
  }
  return [shiftDate, shift]
}
export function shiftFromHour(hour: number): Shift {
  let shift: Shift
  if (hour === FIRST_SHIFT_START_HOUR) {
    shift = Shift.First
  } else if (hour === SECOND_SHIFT_START_HOUR) {
    shift = Shift.Second
  } else if (hour === THIRD_SHIFT_START_HOUR) {
    shift = Shift.Third
  } else {
    throw new Error(`Invalid shift start hour: ${hour}`)
  }
  return shift
}
export function isMultiShiftSlot(
  startDate: DateTime,
  endDate: DateTime | undefined
): boolean {
  if (!endDate) return false
  // A slot is considered multi-shift if it's 23 hours or longer.
  return endDate.diff(startDate, 'hours').hours >= 23
}

/**
 * Determines the display date for a shift group header.
 *
 * Third shift (3am-6am) is the tail end of the previous day's overnight shift,
 * so it displays under the previous day. For other shifts, use the appointment date.
 *
 * @param shift - The shift type (First, Second, or Third)
 * @param shiftDate - The grouping date (for 3rd shift, this is already the previous day)
 * @param appointmentDate - The actual date of the appointment
 * @returns The date to display in shift group headers
 */
export function getDisplayDateForShift(
  shift: Shift,
  shiftDate: DateTime,
  appointmentDate: DateTime
): DateTime {
  return shift === Shift.Third ? shiftDate : appointmentDate.startOf('day')
}

export function shiftNameFromTime(
  dt: DateTime,
  endDate: DateTime | undefined
): string {
  if (isMultiShiftSlot(dt, endDate)) {
    return multiShiftSlotKey(dt)
  }
  // Bucket by the actual shift, not the slot's calendar day. shiftFromTime already
  // resolves overnight slots to the day the shift *started*: a 6/16 00:30 slot belongs
  // to 6/15's 2nd (night) shift, so its key must be "6/15 - 2nd shift", not the
  // calendar-day "6/16 - 2nd shift". This keeps after-midnight slots (and booked
  // appointments, which key off this too) under the same shift terminals like FMS
  // group them under — previously they landed a day late under the next day's 2nd shift.
  const [shiftDate, shift] = shiftFromTime(dt)
  return shiftFromTimeAndShift(shiftDate, shift)
}

/**
 * The first day column a shift-bucketed table (e.g. the Empties table) should render.
 *
 * Normally this is the start of the calendar day. But while an overnight 2nd shift is
 * still running (before 6am) "now" belongs to the *previous* day's shift, and that
 * shift's after-midnight slots key to the previous day (see shiftNameFromTime). So the
 * first column must reach back to that day, otherwise those slots map to a column that
 * isn't rendered and disappear from the table.
 *
 * @param now - The current time.
 * @param startOfDay - Start of the current calendar day.
 */
export function firstShiftColumnDate(
  now: DateTime,
  startOfDay: DateTime
): DateTime {
  const [shiftDayOfNow] = shiftFromTime(now)
  return shiftDayOfNow < startOfDay ? shiftDayOfNow : startOfDay
}

export function shiftStartFromTime(dt: DateTime): DateTime {
  // Returns "real" time of shift start, so third shift will start on the next day
  let shiftStart: DateTime
  const startHour = getShiftStartHour(dt)
  if (dt.hour < THIRD_SHIFT_START_HOUR) {
    // Adjust for overnight shifts
    shiftStart = dt
      .minus({ days: 1 })
      .set({ hour: startHour, minute: 0, second: 0 })
  } else {
    shiftStart = dt.set({ hour: startHour, minute: 0, second: 0 })
  }
  return shiftStart
}

function getShiftStartHour(dt: DateTime): number {
  if (dt.hour >= FIRST_SHIFT_START_HOUR && dt.hour < SECOND_SHIFT_START_HOUR) {
    return FIRST_SHIFT_START_HOUR
  } else if (
    dt.hour >= SECOND_SHIFT_START_HOUR ||
    dt.hour < THIRD_SHIFT_START_HOUR
  ) {
    return SECOND_SHIFT_START_HOUR
  } else {
    return THIRD_SHIFT_START_HOUR
  }
}

export function shiftToInteger(shift: Shift): number {
  switch (shift) {
    case Shift.First:
      return 1
    case Shift.Second:
      return 2
    case Shift.Third:
      return 3
  }
}

export function sortableShiftKey(shiftDate: DateTime, shift: Shift): string {
  const shiftInt = shiftToInteger(shift)
  return `${shiftDate.toFormat('yyyy-MM-dd')}--${shiftInt}`
}

export function shiftWindowStart(shiftDate: DateTime, shift: Shift): DateTime {
  switch (shift) {
    case Shift.First:
      return shiftDate.set({ hour: FIRST_SHIFT_START_HOUR })
    case Shift.Second:
      // Note: For second shift appointments before 3am, shiftDate is the appointment date
      // but the shift actually started the previous day. ShiftGroup.addAppointment() will
      // recalculate the window based on actual appointment times to handle this case correctly.
      return shiftDate.set({ hour: SECOND_SHIFT_START_HOUR })
    case Shift.Third:
      // shiftDate is now the previous day (for sorting), so the actual start
      // is the next day at 3am. E.g., shiftDate=Tue → window starts Wed 3am.
      return shiftDate.plus({ days: 1 }).set({ hour: THIRD_SHIFT_START_HOUR })
  }
}

export function shiftWindowEnd(shiftDate: DateTime, shift: Shift): DateTime {
  switch (shift) {
    case Shift.First:
      return shiftDate.set({ hour: SECOND_SHIFT_START_HOUR })
    case Shift.Second:
      return shiftDate.plus({ days: 1 }).set({ hour: THIRD_SHIFT_START_HOUR })
    case Shift.Third:
      // shiftDate is now the previous day (for sorting), so the actual end
      // is the next day at 6am. E.g., shiftDate=Tue → window ends Wed 6am.
      return shiftDate.plus({ days: 1 }).set({ hour: FIRST_SHIFT_START_HOUR })
  }
}

/**
 * Calculate the window end time from the actual shift start time.
 * This is used when we have the actual shift start DateTime (e.g., Monday 5pm)
 * rather than just the shift date (e.g., Monday start of day).
 */
export function shiftWindowEndFromStart(
  shiftStart: DateTime,
  shift: Shift
): DateTime {
  switch (shift) {
    case Shift.First:
      return shiftStart.set({ hour: SECOND_SHIFT_START_HOUR })
    case Shift.Second:
      return shiftStart.plus({ days: 1 }).set({ hour: THIRD_SHIFT_START_HOUR })
    case Shift.Third:
      // Third shift is 3am-6am on the same day
      return shiftStart.set({ hour: FIRST_SHIFT_START_HOUR })
  }
}
